import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { ChevronUp, ChevronDown, Trash2, Plus } from "lucide-react";
import ImageUpload from "@/admin/ImageUpload";

const inp = "w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none";

// Featured batch cards on the homepage. Each card points at a real batch from Class Setup.
export default function HomepageBatches({ items, onChange }) {
  const [batches, setBatches] = useState([]);
  useEffect(() => { api.get("/admin/batches").then(r => setBatches(r.data || [])); }, []);
  const list = items || [];
  const set = (next) => onChange(next.map((x, i) => ({ ...x, order: i + 1 })));
  const update = (i, patch) => set(list.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const move = (i, dir) => { const to = i + dir; if (to < 0 || to >= list.length) return; const arr = [...list]; [arr[i], arr[to]] = [arr[to], arr[i]]; set(arr); };
  const used = new Set(list.map(x => x.batch_id));
  const available = batches.filter(b => b.active !== false);

  return (
    <div className="card-surface p-6" data-testid="homepage-batches">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow">Featured batches</div>
          <p className="text-zinc-500 text-xs mt-1">Cards shown in the homepage "A class for every age" section. Pick a batch from Class Setup, add a photo. Visitors land on that batch's <strong>current term</strong>. Leave empty to show all batches.</p>
        </div>
        <button onClick={() => set([...list, { batch_id: "", image: "", tagline: "" }])} className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-1 shrink-0" data-testid="featured-add">
          <Plus size={12}/> Add card
        </button>
      </div>
      {list.length === 0 && <div className="text-zinc-600 text-sm" data-testid="featured-empty">No featured cards — homepage shows every active batch.</div>}
      <div className="space-y-4">
        {list.map((it, i) => {
          const b = batches.find(x => x.id === it.batch_id);
          return (
            <div key={`${it.batch_id || "new"}-${i}`} className="border border-white/10 p-4 grid md:grid-cols-[1fr_1fr_auto] gap-4" data-testid={`featured-card-${i}`}>
              <div className="space-y-3">
                <div>
                  <label className="eyebrow block mb-1">Batch</label>
                  <select value={it.batch_id || ""} onChange={e => update(i, { batch_id: e.target.value })} className={inp} data-testid={`featured-batch-${i}`}>
                    <option value="">Select a batch…</option>
                    {available.map(x => <option key={x.id} value={x.id} disabled={used.has(x.id) && x.id !== it.batch_id}>{x.name}{x.age_range ? ` · ${x.age_range}` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-1">Tagline (optional)</label>
                  <input value={it.tagline || ""} onChange={e => update(i, { tagline: e.target.value })} placeholder={b?.description ? b.description.slice(0, 60) : "Short line under the name"} className={inp} data-testid={`featured-tagline-${i}`}/>
                </div>
              </div>
              <div>
                <ImageUpload label="Photo" value={it.image || b?.image || ""} onChange={v => update(i, { image: v })} testId={`featured-image-${i}`}/>
                {!it.image && b?.image && <div className="text-[10px] text-zinc-600 mt-1">Using the batch photo from Class Setup.</div>}
              </div>
              <div className="flex md:flex-col gap-1 items-center justify-center">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 hover:text-red-500" data-testid={`featured-up-${i}`}><ChevronUp size={16}/></button>
                <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="p-1 disabled:opacity-30 hover:text-red-500" data-testid={`featured-down-${i}`}><ChevronDown size={16}/></button>
                <button onClick={() => set(list.filter((_, idx) => idx !== i))} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`featured-remove-${i}`}><Trash2 size={15}/></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
